import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Toast } from "primereact/toast";
import {
  FaUserPlus,
  FaSearch,
  FaTimes,
  FaSyncAlt,
  FaCheck,
  FaBan,
  FaSearchPlus,
  FaSearchMinus,
  FaRedo,
  FaExternalLinkAlt,
  FaCopy,
  FaPhoneAlt,
  FaEnvelope,
  FaWhatsapp,
  FaExclamationTriangle,
  FaInbox,
  FaRupeeSign,
  FaHourglassHalf,
  FaClone,
  FaUserFriends,
  FaUserSlash,
  FaChevronUp,
  FaChevronDown,
  FaHandHoldingUsd,
  FaBoxOpen,
  FaListUl,
  FaImage,
  FaRegCheckSquare,
  FaRegSquare,
  FaHistory,
} from "react-icons/fa";
import { useBaseUrl } from "../Contexts/BaseUrlContext";
import { useStateContext } from "../Contexts/ContextProvider";

/* ================= styles ================= */

const CSS = `
@keyframes crFadeUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }
@keyframes crFade { from { opacity: 0; } to { opacity: 1; } }
@keyframes crZoom { from { opacity: 0; transform: scale(.96); } to { opacity: 1; transform: none; } }
@keyframes crSwap { from { opacity: 0; transform: translateX(16px); } to { opacity: 1; transform: none; } }
@keyframes crOut { to { opacity: 0; transform: translateX(-30px); } }
.cr-in { animation: crFadeUp .5s cubic-bezier(.2,.7,.2,1) both; }
.cr-fade { animation: crFade .2s ease both; }
.cr-zoom { animation: crZoom .25s cubic-bezier(.2,.8,.2,1) both; }
.cr-swap { animation: crSwap .3s cubic-bezier(.2,.8,.2,1) both; }
.cr-out { animation: crOut .45s ease forwards; }
.cr-press { transition: transform .15s ease, background-color .2s ease, border-color .2s ease, color .2s ease; }
.cr-press:hover { transform: translateY(-1px); }
.cr-press:active { transform: scale(.96); }
.cr-mark { background: #fde68a; color: inherit; border-radius: 3px; padding: 0 1px; }
.cr-grid { background-image: linear-gradient(rgba(148,163,184,.12) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,.12) 1px, transparent 1px); background-size: 22px 22px; }
@media (prefers-reduced-motion: reduce) {
  .cr-in, .cr-fade, .cr-zoom, .cr-swap, .cr-out { animation: none; }
  .cr-press:hover, .cr-press:active { transform: none; }
}
`;

const CARD = "bg-white dark:bg-secondary-dark-bg rounded-2xl border border-slate-200 dark:border-slate-700";
const INPUT =
  "rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500";

const REASONS = ["Payment not received", "Wrong amount paid", "Payment screenshot is not clear", "Fake or edited screenshot", "Duplicate request", "Wrong contact details"];

/* ================= helpers ================= */

const DAY = 86400000;
const num = (v) => Number(v || 0);
const money = (n) => `₹${num(n).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const parseAny = (s) => {
  if (!s) return null;
  const d = new Date(String(s).replace(" ", "T"));
  return Number.isNaN(d.getTime()) ? null : d;
};
const fmtDate = (d) => (d ? d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-");
const fmtTime = (d) => (d ? d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "");
const waiting = (d) => {
  if (!d) return "";
  const h = Math.floor((Date.now() - d.getTime()) / 3600000);
  if (h < 1) return "Just now";
  if (h < 24) return `${h} hr waiting`;
  const days = Math.floor(h / 24);
  return `${days} day${days > 1 ? "s" : ""} waiting`;
};
const nm = (s) => (s && String(s).trim() ? String(s).trim() : null);
const noRef = (r) => !r.user_id || r.user_id === "null" || r.user_id === "NULL";
const hasProof = (url) => url && !/\/$/.test(url) && !/\/(null|0)$/.test(url);
const phoneKey = (p) => String(p || "").replace(/\D/g, "").slice(-10);
const waLink = (phone) => {
  const d = phoneKey(phone);
  return d ? `https://wa.me/91${d}` : null;
};

const Hi = ({ text, q }) => {
  const s = String(text ?? "");
  if (!q || !s) return <>{s}</>;
  const i = s.toLowerCase().indexOf(q.toLowerCase());
  if (i < 0) return <>{s}</>;
  return (
    <>
      {s.slice(0, i)}
      <mark className="cr-mark">{s.slice(i, i + q.length)}</mark>
      {s.slice(i + q.length)}
    </>
  );
};

/* ================= page ================= */

const CustomerRequest = () => {
  const [requests, setRequests] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [packages, setPackages] = useState({});
  const [loading, setLoading] = useState(false);

  const [query, setQuery] = useState("");
  const [flag, setFlag] = useState("all");
  const [sortBy, setSortBy] = useState("oldest");
  const [activeId, setActiveId] = useState(null);
  const [checked, setChecked] = useState([]);
  const [checks, setChecks] = useState({});

  const [zoom, setZoom] = useState(1);
  const [rotate, setRotate] = useState(0);
  const [confirm, setConfirm] = useState(null);
  const [reason, setReason] = useState(REASONS[0]);
  const [customReason, setCustomReason] = useState("");
  const [working, setWorking] = useState(false);
  const [leaving, setLeaving] = useState({});

  const toast = useRef(null);
  const searchRef = useRef(null);
  const navigate = useNavigate();
  const { baseUrl } = useBaseUrl();
  const { currentMode } = useStateContext();

  /* ----- API (same actions) ----- */

  const post = useCallback(
    async (action, extra = {}) => {
      const fd = new FormData();
      fd.append("action", action);
      Object.entries(extra).forEach(([k, v]) => fd.append(k, v));
      const res = await fetch(`${baseUrl}/customer.php`, { method: "POST", body: fd, credentials: "include" });
      return res.json();
    },
    [baseUrl]
  );

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [r, c, p] = await Promise.all([post("customerRequestList"), post("customerList").catch(() => null), post("packageList").catch(() => null)]);
      const list = Array.isArray(r?.requests) ? r.requests : [];
      setRequests(list);
      setCustomers(Array.isArray(c?.customers) ? c.customers : []);
      const pm = {};
      (p?.data || []).forEach((x) => (pm[String(x.pkg_id)] = x));
      setPackages(pm);
      setActiveId((cur) => (list.some((x) => x.cust_id === cur) ? cur : list[0]?.cust_id || null));
    } catch (err) {
      console.error(err);
      toast.current?.show({ severity: "error", summary: "Error", detail: "Failed to fetch customer requests" });
    }
    setLoading(false);
  }, [post]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  /* ----- derived ----- */

  const q = query.trim();

  // same phone + same package among open requests
  const dupInfo = useMemo(() => {
    const groups = {};
    requests.forEach((r) => {
      const k = `${phoneKey(r.contact_num)}-${r.pkg_id}`;
      if (!groups[k]) groups[k] = [];
      groups[k].push(r.cust_id);
    });
    const m = {};
    Object.values(groups).forEach((ids) => {
      if (ids.length > 1) ids.forEach((id) => (m[id] = ids.filter((x) => x !== id)));
    });
    return m;
  }, [requests]);

  const pastOrders = useMemo(() => {
    const m = {};
    customers.forEach((c) => {
      const k = phoneKey(c.contact_num);
      if (!k) return;
      if (!m[k]) m[k] = [];
      m[k].push(c);
    });
    return m;
  }, [customers]);

  const refGone = (r) => !noRef(r) && !nm(r.user_name);
  const pkgOf = (r) => packages[String(r.pkg_id)] || {};
  const commissionOf = (r) => (noRef(r) ? 0 : (num(r.pkg_price) * num(pkgOf(r).reffer_comission)) / 100);

  const stats = useMemo(() => {
    const now = Date.now();
    return {
      count: requests.length,
      value: requests.reduce((s, r) => s + num(r.pkg_price), 0),
      dup: Object.keys(dupInfo).length,
      returning: requests.filter((r) => pastOrders[phoneKey(r.contact_num)]).length,
      old: requests.filter((r) => {
        const d = parseAny(r.date_time);
        return d && now - d.getTime() > DAY;
      }).length,
    };
  }, [requests, dupInfo, pastOrders]);

  const filtered = useMemo(() => {
    const ql = q.toLowerCase();
    const now = Date.now();
    let l = requests.filter((r) => {
      if (flag === "dup" && !dupInfo[r.cust_id]) return false;
      if (flag === "returning" && !pastOrders[phoneKey(r.contact_num)]) return false;
      if (flag === "referred" && noRef(r)) return false;
      if (flag === "self" && !noRef(r)) return false;
      if (flag === "old") {
        const d = parseAny(r.date_time);
        if (!d || now - d.getTime() <= DAY) return false;
      }
      if (!ql) return true;
      return [r.cust_id, r.name, r.contact_num, r.email_id, r.user_name, r.user_id, r.pkg_name].map((v) => String(v ?? "").toLowerCase()).some((v) => v.includes(ql));
    });
    l = [...l].sort((a, b) => {
      if (sortBy === "price") return num(b.pkg_price) - num(a.pkg_price);
      const da = parseAny(a.date_time)?.getTime() || 0;
      const db = parseAny(b.date_time)?.getTime() || 0;
      return sortBy === "oldest" ? da - db : db - da;
    });
    return l;
  }, [requests, q, flag, sortBy, dupInfo, pastOrders]);

  const active = filtered.find((r) => r.cust_id === activeId) || filtered[0] || null;
  const activeIndex = active ? filtered.findIndex((r) => r.cust_id === active.cust_id) : -1;

  useEffect(() => {
    setZoom(1);
    setRotate(0);
  }, [activeId]);

  useEffect(() => {
    const valid = new Set(requests.map((r) => r.cust_id));
    setChecked((c) => c.filter((id) => valid.has(id)));
  }, [requests]);

  const go = (step) => {
    if (!filtered.length) return;
    const i = activeIndex < 0 ? 0 : Math.min(filtered.length - 1, Math.max(0, activeIndex + step));
    setActiveId(filtered[i].cust_id);
  };

  const openConfirm = (type, list) => {
    const isDup = list.length === 1 && dupInfo[list[0].cust_id];
    setReason(isDup ? "Duplicate request" : REASONS[0]);
    setCustomReason("");
    setConfirm({ type, list });
  };

  const runConfirm = async () => {
    if (!confirm) return;
    const { type, list } = confirm;
    const finalReason = (reason === "__custom" ? customReason : reason).trim();
    setWorking(true);
    let ok = 0;
    const errors = [];
    for (const r of list) {
      try {
        const res = type === "accept" ? await post("customerAccept", { cust_id: r.cust_id }) : await post("cancelCustomerRequest", { cust_id: r.cust_id, reason: finalReason });
        if (res?.error !== 0) throw new Error(typeof res?.error === "string" ? res.error : res?.message || "Failed");
        ok += 1;
        setLeaving((l) => ({ ...l, [r.cust_id]: type }));
      } catch (e) {
        errors.push(`${r.name}: ${e.message}`);
      }
    }
    setWorking(false);
    setConfirm(null);
    setChecked([]);
    if (ok)
      toast.current?.show({
        severity: "success",
        summary: type === "accept" ? "Accepted" : "Cancelled",
        detail:
          type === "accept"
            ? `${ok} customer${ok > 1 ? "s" : ""} added. Email sent${list.some((r) => !noRef(r)) ? " and referrer commission paid" : ""}.`
            : `${ok} request${ok > 1 ? "s" : ""} cancelled. Customer emailed with the reason.`,
      });
    if (errors.length) toast.current?.show({ severity: "error", summary: "Some failed", detail: errors.slice(0, 3).join(" | "), life: 7000 });
    // jump to the next request in the queue
    const next = filtered.find((r, i) => i >= activeIndex && !list.some((x) => x.cust_id === r.cust_id));
    setTimeout(() => {
      setLeaving({});
      if (next) setActiveId(next.cust_id);
      fetchAll();
    }, 500);
  };

  useEffect(() => {
    const onKey = (e) => {
      const typing = ["INPUT", "TEXTAREA", "SELECT"].includes(document.activeElement?.tagName);
      if (confirm) {
        if (e.key === "Escape" && !working) setConfirm(null);
        return;
      }
      if (typing) return;
      if (e.key === "/") {
        e.preventDefault();
        searchRef.current?.focus();
      } else if (e.key === "j" || e.key === "ArrowDown") {
        e.preventDefault();
        go(1);
      } else if (e.key === "k" || e.key === "ArrowUp") {
        e.preventDefault();
        go(-1);
      } else if ((e.key === "a" || e.key === "A") && active) openConfirm("accept", [active]);
      else if ((e.key === "r" || e.key === "R") && active) openConfirm("cancel", [active]);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const copyText = (t, label = "Copied") => {
    navigator.clipboard?.writeText(String(t));
    toast.current?.show({ severity: "info", summary: label, detail: String(t), life: 1500 });
  };

  const tick = (key) => active && setChecks((c) => ({ ...c, [active.cust_id]: { ...(c[active.cust_id] || {}), [key]: !(c[active.cust_id] || {})[key] } }));

  /* ================= render ================= */

  const a = active;
  const aChecks = a ? checks[a.cust_id] || {} : {};
  const aPast = a ? pastOrders[phoneKey(a.contact_num)] || [] : [];
  const aDup = a ? dupInfo[a.cust_id] || [] : [];
  const aPkg = a ? pkgOf(a) : {};
  const doneCount = ["paid", "details", "notDup"].filter((k) => aChecks[k]).length;

  return (
    <div style={currentMode === "Dark" ? { backgroundColor: "#20232A" } : {}} className="w-full p-4 sm:p-6 lg:p-8 bg-slate-50 min-h-screen">
      <style>{CSS}</style>
      <Toast ref={toast} />

      {/* Header */}
      <div className="cr-in flex flex-wrap items-end justify-between gap-4 mb-5">
        <div>
          <p className="text-sm text-slate-500 dark:text-slate-400">Customer</p>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">Customer Requests</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Check the payment screenshot, then accept to add the customer or cancel with a reason.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={() => navigate("/customer/list")} className="cr-press inline-flex items-center gap-2 h-9 px-3.5 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-secondary-dark-bg text-sm font-medium text-slate-700 dark:text-slate-200">
            <FaListUl size={12} /> Customers
          </button>
          <button type="button" onClick={() => navigate("/customer-packages")} className="cr-press inline-flex items-center gap-2 h-9 px-3.5 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-secondary-dark-bg text-sm font-medium text-slate-700 dark:text-slate-200">
            <FaBoxOpen size={12} /> Packages
          </button>
          <button type="button" onClick={fetchAll} disabled={loading} className="cr-press inline-flex items-center gap-2 h-9 px-4 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold disabled:opacity-60">
            <FaSyncAlt size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {/* Stats (clickable filters) */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {[
          { id: "all", label: "Waiting requests", value: stats.count, icon: FaUserPlus, tint: "bg-orange-50 text-orange-600 dark:bg-orange-900/30 dark:text-orange-300" },
          { id: "all", label: "Value to verify", value: money(stats.value), icon: FaRupeeSign, tint: "bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-300" },
          { id: "dup", label: "Possible duplicates", value: stats.dup, icon: FaClone, tint: "bg-rose-50 text-rose-600 dark:bg-rose-900/30 dark:text-rose-300" },
          { id: "returning", label: "Returning customers", value: stats.returning, icon: FaHistory, tint: "bg-violet-50 text-violet-600 dark:bg-violet-900/30 dark:text-violet-300" },
          { id: "old", label: "Waiting over 1 day", value: stats.old, icon: FaHourglassHalf, tint: "bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-300" },
        ].map((c, i) => {
          const Icon = c.icon;
          const on = flag === c.id && c.id !== "all";
          return (
            <button key={c.label} type="button" onClick={() => setFlag(on ? "all" : c.id)} className={`${CARD} cr-in cr-press p-4 flex items-center gap-3 text-left ${on ? "ring-2 ring-orange-400" : ""}`} style={{ animationDelay: `${i * 50}ms` }}>
              <span className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${c.tint}`}>
                <Icon />
              </span>
              <div className="min-w-0">
                <p className="text-xl font-bold text-slate-900 dark:text-white tabular-nums truncate">{loading && !requests.length ? "–" : c.value}</p>
                <p className="text-xs text-slate-500 truncate">{c.label}</p>
              </div>
            </button>
          );
        })}
      </div>

      {/* Station */}
      <div className="mt-4 grid grid-cols-1 xl:grid-cols-12 gap-4 items-start">
        {/* Queue */}
        <section className={`${CARD} cr-in xl:col-span-4 overflow-hidden`} style={{ animationDelay: "200ms" }}>
          <div className="p-3 border-b border-slate-200 dark:border-slate-700 space-y-2">
            <div className="relative">
              <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={13} />
              <input ref={searchRef} value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Name, phone, email, ID, referrer" className={`${INPUT} w-full h-10 pl-10 pr-9`} />
              {query && (
                <button type="button" onClick={() => setQuery("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-slate-700" aria-label="Clear search">
                  <FaTimes size={12} />
                </button>
              )}
            </div>
            <div className="flex gap-2">
              <select value={flag} onChange={(e) => setFlag(e.target.value)} className={`${INPUT} h-9 px-2 flex-1`}>
                <option value="all">All requests</option>
                <option value="dup">Possible duplicates</option>
                <option value="returning">Returning customers</option>
                <option value="referred">Brought by a user</option>
                <option value="self">Self (no referrer)</option>
                <option value="old">Waiting over 1 day</option>
              </select>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className={`${INPUT} h-9 px-2`}>
                <option value="oldest">Oldest first</option>
                <option value="newest">Newest first</option>
                <option value="price">Highest price</option>
              </select>
            </div>
            <div className="flex items-center justify-between text-xs">
              <label className="inline-flex items-center gap-2 text-slate-600 dark:text-slate-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filtered.length > 0 && filtered.every((r) => checked.includes(r.cust_id))}
                  onChange={() => setChecked(filtered.every((r) => checked.includes(r.cust_id)) ? [] : filtered.map((r) => r.cust_id))}
                  className="w-4 h-4 accent-orange-500"
                />
                Select all ({filtered.length})
              </label>
              <span className="text-slate-400">J / K to move</span>
            </div>
            {checked.length > 0 && (
              <div className="cr-fade flex items-center gap-2">
                <button type="button" onClick={() => openConfirm("accept", requests.filter((r) => checked.includes(r.cust_id)))} className="cr-press flex-1 h-9 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold inline-flex items-center justify-center gap-1.5">
                  <FaCheck size={10} /> Accept {checked.length}
                </button>
                <button type="button" onClick={() => openConfirm("cancel", requests.filter((r) => checked.includes(r.cust_id)))} className="cr-press flex-1 h-9 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold inline-flex items-center justify-center gap-1.5">
                  <FaBan size={10} /> Cancel {checked.length}
                </button>
              </div>
            )}
          </div>

          <ul className="max-h-[70vh] overflow-y-auto divide-y divide-slate-100 dark:divide-slate-700">
            {loading && !requests.length &&
              [0, 1, 2].map((i) => (
                <li key={i} className="p-3">
                  <div className="h-14 rounded-lg bg-slate-100 dark:bg-slate-700 animate-pulse" />
                </li>
              ))}
            {!loading && !filtered.length && (
              <li className="px-4 py-12 text-center">
                <FaInbox className="mx-auto text-slate-300" size={30} />
                <p className="mt-2 text-sm font-medium text-slate-700 dark:text-slate-200">{requests.length ? "Nothing matches" : "All caught up"}</p>
                {!requests.length && <p className="text-xs text-slate-500">New customer requests will show up here.</p>}
              </li>
            )}
            {filtered.map((r) => {
              const d = parseAny(r.date_time);
              const on = a && r.cust_id === a.cust_id;
              const old = d && Date.now() - d.getTime() > DAY;
              return (
                <li key={r.cust_id} className={leaving[r.cust_id] ? "cr-out" : ""}>
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => setActiveId(r.cust_id)}
                    onKeyDown={(e) => e.key === "Enter" && setActiveId(r.cust_id)}
                    className={`flex items-start gap-3 px-3 py-3 cursor-pointer transition-colors border-l-4 ${on ? "bg-orange-50 dark:bg-orange-900/10 border-orange-500" : "border-transparent hover:bg-slate-50 dark:hover:bg-slate-700/40"}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked.includes(r.cust_id)}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => setChecked((c) => (c.includes(r.cust_id) ? c.filter((x) => x !== r.cust_id) : [...c, r.cust_id]))}
                      className="mt-1 w-4 h-4 accent-orange-500"
                      aria-label="Select request"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-semibold text-slate-900 dark:text-white truncate">
                          <Hi text={r.name} q={q} />
                        </p>
                        <span className="text-sm font-bold tabular-nums text-slate-900 dark:text-white">{money(r.pkg_price)}</span>
                      </div>
                      <p className="text-xs text-slate-500 tabular-nums truncate">
                        <Hi text={r.contact_num} q={q} /> • <Hi text={r.pkg_name} q={q} />
                      </p>
                      <div className="mt-1 flex flex-wrap items-center gap-1.5">
                        {dupInfo[r.cust_id] && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-rose-100 text-rose-700">DUPLICATE?</span>}
                        {pastOrders[phoneKey(r.contact_num)] && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-violet-100 text-violet-700">RETURNING</span>}
                        {noRef(r) ? (
                          <span className="text-[10px] font-semibold text-teal-700">Self</span>
                        ) : (
                          <span className={`text-[10px] font-semibold ${refGone(r) ? "text-rose-600" : "text-slate-600 dark:text-slate-300"}`}>via {nm(r.user_name) || "unknown user"}</span>
                        )}
                        <span className={`text-[10px] ml-auto ${old ? "text-amber-700 font-semibold" : "text-slate-400"}`}>{waiting(d)}</span>
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </section>

        {/* Proof + decision */}
        {a ? (
          <section key={a.cust_id} className="cr-swap xl:col-span-8 grid grid-cols-1 lg:grid-cols-5 gap-4">
            {/* Proof */}
            <div className={`${CARD} lg:col-span-3 overflow-hidden flex flex-col`}>
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-200 dark:border-slate-700">
                <p className="text-sm font-semibold text-slate-900 dark:text-white inline-flex items-center gap-2">
                  <FaImage className="text-orange-500" /> Payment proof
                </p>
                <div className="flex gap-1">
                  <button type="button" onClick={() => setZoom((z) => Math.max(1, z - 0.5))} className="cr-press w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-700 flex items-center justify-center" aria-label="Zoom out">
                    <FaSearchMinus size={11} />
                  </button>
                  <button type="button" onClick={() => setZoom((z) => Math.min(4, z + 0.5))} className="cr-press w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-700 flex items-center justify-center" aria-label="Zoom in">
                    <FaSearchPlus size={11} />
                  </button>
                  <button type="button" onClick={() => setRotate((x) => (x + 90) % 360)} className="cr-press w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-700 flex items-center justify-center" aria-label="Rotate">
                    <FaRedo size={10} />
                  </button>
                  {hasProof(a.payment_proof) && (
                    <a href={a.payment_proof} target="_blank" rel="noreferrer" className="cr-press w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-700 flex items-center justify-center" aria-label="Open original">
                      <FaExternalLinkAlt size={10} />
                    </a>
                  )}
                </div>
              </div>
              <div className="cr-grid relative flex-1 min-h-[420px] overflow-auto flex items-center justify-center p-4 bg-slate-50 dark:bg-slate-900/40">
                {hasProof(a.payment_proof) ? (
                  <img
                    src={a.payment_proof}
                    alt="Payment proof"
                    onClick={() => setZoom((z) => (z === 1 ? 2 : 1))}
                    className="max-w-full max-h-[62vh] object-contain rounded-lg shadow-lg cursor-zoom-in transition-transform duration-300"
                    style={{ transform: `scale(${zoom}) rotate(${rotate}deg)` }}
                  />
                ) : (
                  <p className="text-sm text-slate-400 flex flex-col items-center gap-2">
                    <FaImage size={28} /> No payment proof uploaded
                  </p>
                )}
                <span className="absolute left-4 bottom-4 rounded-xl bg-slate-900/85 text-white px-3 py-2 text-xs shadow-lg">
                  Expected amount <b className="block text-2xl tabular-nums">{money(a.pkg_price)}</b>
                </span>
              </div>
            </div>

            {/* Decision panel */}
            <div className="lg:col-span-2 space-y-3">
              <div className={`${CARD} p-4`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-xs text-slate-500">
                      Request {activeIndex + 1} of {filtered.length}
                    </p>
                    <h2 className="text-lg font-bold text-slate-900 dark:text-white truncate">{a.name}</h2>
                    <button type="button" onClick={() => copyText(a.cust_id, "Customer ID copied")} className="text-xs text-slate-500 tabular-nums hover:text-slate-800 inline-flex items-center gap-1">
                      #{a.cust_id} <FaCopy size={8} />
                    </button>
                  </div>
                  <div className="flex flex-col gap-1">
                    <button type="button" onClick={() => go(-1)} disabled={activeIndex <= 0} className="cr-press w-8 h-8 rounded-lg border border-slate-200 dark:border-slate-600 flex items-center justify-center disabled:opacity-30" aria-label="Previous request">
                      <FaChevronUp size={10} />
                    </button>
                    <button type="button" onClick={() => go(1)} disabled={activeIndex >= filtered.length - 1} className="cr-press w-8 h-8 rounded-lg border border-slate-200 dark:border-slate-600 flex items-center justify-center disabled:opacity-30" aria-label="Next request">
                      <FaChevronDown size={10} />
                    </button>
                  </div>
                </div>
                <div className="mt-3 space-y-1.5 text-sm">
                  <p className="flex items-center gap-2 text-slate-700 dark:text-slate-200">
                    <FaPhoneAlt size={11} className="text-slate-400" /> {a.contact_num || "-"}
                    {a.contact_num && (
                      <button type="button" onClick={() => copyText(a.contact_num, "Phone copied")} className="text-slate-400 hover:text-slate-700" aria-label="Copy phone">
                        <FaCopy size={9} />
                      </button>
                    )}
                  </p>
                  <p className="flex items-center gap-2 text-slate-700 dark:text-slate-200 break-all">
                    <FaEnvelope size={11} className="text-slate-400 shrink-0" /> {a.email_id || "-"}
                  </p>
                  <p className="text-xs text-slate-500">
                    Sent {fmtDate(parseAny(a.date_time))}, {fmtTime(parseAny(a.date_time))} ({waiting(parseAny(a.date_time))})
                  </p>
                </div>
                {waLink(a.contact_num) && (
                  <a href={waLink(a.contact_num)} target="_blank" rel="noreferrer" className="cr-press mt-3 inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-emerald-50 text-emerald-700 text-xs font-semibold">
                    <FaWhatsapp /> Ask on WhatsApp
                  </a>
                )}
              </div>

              <div className={`${CARD} p-4`}>
                <p className="text-xs font-semibold text-slate-500">Package</p>
                <div className="flex items-baseline justify-between gap-2">
                  <p className="font-bold text-slate-900 dark:text-white">{a.pkg_name || `Package ${a.pkg_id}`}</p>
                  <p className="text-xl font-black tabular-nums text-orange-600">{money(a.pkg_price)}</p>
                </div>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  {[aPkg.pkg_type, num(aPkg.watch_time) ? `${aPkg.watch_time} watch` : null, num(aPkg.likes) ? `${aPkg.likes} likes` : null, num(aPkg.comments) ? `${aPkg.comments} comments` : null, num(aPkg.share) ? `${aPkg.share} shares` : null]
                    .filter(Boolean)
                    .join(" • ") || "Package details not found"}
                </p>
                <div className="mt-3 rounded-xl bg-slate-50 dark:bg-slate-800/60 p-3 text-sm">
                  <p className="text-xs text-slate-500 inline-flex items-center gap-1.5">
                    <FaUserFriends size={10} /> Joined by
                  </p>
                  {noRef(a) ? (
                    <p className="font-semibold text-teal-700">Self (no referrer, no commission)</p>
                  ) : (
                    <>
                      <p className={`font-semibold ${refGone(a) ? "text-rose-600" : "text-slate-900 dark:text-white"}`}>
                        {refGone(a) ? (
                          <span className="inline-flex items-center gap-1">
                            <FaUserSlash size={11} /> Unknown user ({a.user_id})
                          </span>
                        ) : (
                          <button type="button" onClick={() => navigate(`/user-profile/${a.user_id}`)} className="hover:underline text-left">
                            {a.user_name} <span className="text-xs text-slate-500 font-normal">({a.user_id})</span>
                          </button>
                        )}
                      </p>
                      <p className="text-xs text-amber-700 mt-1 inline-flex items-center gap-1">
                        <FaHandHoldingUsd size={10} /> Gets {money(commissionOf(a))} ({num(aPkg.reffer_comission)}%) on accept
                      </p>
                    </>
                  )}
                </div>
              </div>

              {aDup.length > 0 && (
                <div className="rounded-2xl border border-rose-200 dark:border-rose-900 bg-rose-50 dark:bg-rose-900/20 p-3 text-xs text-rose-900 dark:text-rose-200">
                  <p className="font-semibold inline-flex items-center gap-1.5">
                    <FaClone size={11} /> Same phone and package sent {aDup.length + 1} times
                  </p>
                  <p className="mt-1">Accept one and cancel the others as "Duplicate request".</p>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {aDup.map((id) => (
                      <button key={id} type="button" onClick={() => setActiveId(id)} className="cr-press px-2 py-1 rounded-lg bg-white dark:bg-slate-800 border border-rose-200 dark:border-rose-800 font-semibold tabular-nums">
                        #{id}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {aPast.length > 0 && (
                <div className="rounded-2xl border border-violet-200 dark:border-violet-900 bg-violet-50 dark:bg-violet-900/20 p-3 text-xs text-violet-900 dark:text-violet-200">
                  <p className="font-semibold inline-flex items-center gap-1.5">
                    <FaHistory size={11} /> Returning customer: {aPast.length} earlier order{aPast.length > 1 ? "s" : ""}
                  </p>
                  <ul className="mt-1 space-y-0.5">
                    {aPast.slice(0, 3).map((c) => (
                      <li key={c.cust_id} className="tabular-nums">
                        #{c.cust_id} • {c.pkg_name} • {fmtDate(parseAny(c.date_time))}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Checklist */}
              <div className={`${CARD} p-4`}>
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-slate-900 dark:text-white">Before you accept</p>
                  <span className={`text-xs font-bold tabular-nums ${doneCount === 3 ? "text-emerald-600" : "text-slate-400"}`}>{doneCount}/3</span>
                </div>
                <div className="mt-2 h-1.5 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden">
                  <div className={`h-full rounded-full transition-all duration-500 ${doneCount === 3 ? "bg-emerald-500" : "bg-orange-400"}`} style={{ width: `${(doneCount / 3) * 100}%` }} />
                </div>
                {[
                  { key: "paid", label: `Screenshot shows ${money(a.pkg_price)} received` },
                  { key: "details", label: "Name and phone look correct" },
                  { key: "notDup", label: aDup.length ? "I checked the duplicates" : "Not a repeat of another request" },
                ].map((c) => (
                  <button key={c.key} type="button" onClick={() => tick(c.key)} className="w-full mt-2 flex items-center gap-2.5 text-left text-sm text-slate-700 dark:text-slate-200">
                    {aChecks[c.key] ? <FaRegCheckSquare className="text-emerald-600 shrink-0" /> : <FaRegSquare className="text-slate-400 shrink-0" />}
                    <span className={aChecks[c.key] ? "line-through text-slate-400" : ""}>{c.label}</span>
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-2 gap-2">
                <button type="button" onClick={() => openConfirm("cancel", [a])} className="cr-press h-12 rounded-xl border-2 border-rose-200 dark:border-rose-800 bg-white dark:bg-secondary-dark-bg text-rose-600 text-sm font-bold inline-flex items-center justify-center gap-2">
                  <FaBan size={12} /> Cancel <kbd className="text-[10px] font-normal opacity-60">R</kbd>
                </button>
                <button type="button" onClick={() => openConfirm("accept", [a])} className={`cr-press h-12 rounded-xl text-white text-sm font-bold inline-flex items-center justify-center gap-2 shadow-sm ${doneCount === 3 ? "bg-emerald-600 hover:bg-emerald-700" : "bg-emerald-500 hover:bg-emerald-600"}`}>
                  <FaCheck size={12} /> Accept <kbd className="text-[10px] font-normal opacity-70">A</kbd>
                </button>
              </div>
            </div>
          </section>
        ) : (
          !loading && (
            <section className={`${CARD} xl:col-span-8 px-5 py-20 text-center`}>
              <FaInbox className="mx-auto text-slate-300" size={40} />
              <p className="mt-3 font-semibold text-slate-700 dark:text-slate-200">No request selected</p>
              <p className="text-sm text-slate-500">When a customer request arrives it opens here for review.</p>
            </section>
          )
        )}
      </div>

      {/* Confirm */}
      {confirm && confirm.list.length > 0 && (
        <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: 1200 }}>
          <div className="cr-fade absolute inset-0 bg-slate-900/60" onClick={() => !working && setConfirm(null)} />
          <div className="cr-zoom relative w-full max-w-md rounded-2xl bg-white dark:bg-secondary-dark-bg shadow-2xl p-5">
            <span className={`w-12 h-12 rounded-2xl flex items-center justify-center ${confirm.type === "accept" ? "bg-emerald-100 text-emerald-600" : "bg-rose-100 text-rose-600"}`}>
              {confirm.type === "accept" ? <FaCheck size={18} /> : <FaExclamationTriangle size={18} />}
            </span>
            <h3 className="mt-3 text-lg font-semibold text-slate-900 dark:text-white">
              {confirm.type === "accept"
                ? confirm.list.length === 1
                  ? `Accept ${confirm.list[0].name}?`
                  : `Accept ${confirm.list.length} requests?`
                : confirm.list.length === 1
                ? `Cancel ${confirm.list[0].name}'s request?`
                : `Cancel ${confirm.list.length} requests?`}
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              {confirm.type === "accept"
                ? "The customer is added to the Customer List and gets a welcome email. If a user brought them, that user gets the commission in their wallet."
                : "The request is removed. The customer gets an email with the reason and the referrer gets a notification."}
            </p>
            <div className="mt-4 max-h-40 overflow-y-auto rounded-xl bg-slate-50 dark:bg-slate-800/60 divide-y divide-slate-100 dark:divide-slate-700">
              {confirm.list.map((r) => (
                <div key={r.cust_id} className="flex items-center justify-between gap-3 p-2.5">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
                      {r.name} {dupInfo[r.cust_id] && <span className="text-[10px] font-bold px-1 rounded bg-rose-100 text-rose-700">DUP?</span>}
                    </p>
                    <p className="text-xs text-slate-500 truncate">
                      {r.pkg_name} • {noRef(r) ? "Self" : `commission ${money(commissionOf(r))}`}
                    </p>
                  </div>
                  <span className="text-sm font-bold tabular-nums">{money(r.pkg_price)}</span>
                </div>
              ))}
            </div>
            {confirm.type === "accept" && confirm.list.some((r) => dupInfo[r.cust_id]) && (
              <p className="mt-3 flex items-start gap-2 rounded-xl bg-amber-50 dark:bg-amber-900/20 text-amber-900 dark:text-amber-200 text-xs p-3">
                <FaExclamationTriangle className="mt-0.5 shrink-0" /> Some of these look like duplicates. Accepting all of them creates extra customers and pays extra commission.
              </p>
            )}
            {confirm.type === "cancel" && (
              <div className="mt-4">
                <p className="text-sm font-medium text-slate-700 dark:text-slate-200 mb-2">Reason (emailed to the customer)</p>
                <div className="flex flex-wrap gap-2">
                  {[...REASONS, "__custom"].map((rs) => (
                    <button
                      key={rs}
                      type="button"
                      onClick={() => setReason(rs)}
                      className={`cr-press h-8 px-3 rounded-full text-xs font-semibold border ${reason === rs ? "bg-rose-600 text-white border-rose-600" : "bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600"}`}
                    >
                      {rs === "__custom" ? "Write my own" : rs}
                    </button>
                  ))}
                </div>
                {reason === "__custom" && <textarea rows={2} value={customReason} onChange={(e) => setCustomReason(e.target.value)} autoFocus placeholder="Write the reason" className={`${INPUT} w-full mt-2 px-3 py-2`} />}
              </div>
            )}
            <div className="flex gap-2 mt-5">
              <button type="button" onClick={() => setConfirm(null)} disabled={working} className="cr-press flex-1 h-11 rounded-xl border border-slate-200 dark:border-slate-600 text-sm font-medium text-slate-700 dark:text-slate-200">
                Go back
              </button>
              <button
                type="button"
                onClick={runConfirm}
                disabled={working || (confirm.type === "cancel" && reason === "__custom" && !customReason.trim())}
                className={`cr-press flex-1 h-11 rounded-xl text-white text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50 ${confirm.type === "accept" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"}`}
              >
                {working ? <FaSyncAlt className="animate-spin" size={12} /> : confirm.type === "accept" ? <FaCheck size={12} /> : <FaBan size={12} />}
                {working ? "Please wait..." : confirm.type === "accept" ? "Yes, accept" : "Yes, cancel"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerRequest;